import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../services/global-environment';

interface LawSample {
  id?: string;
  companyid: string;
  topic: string;
  type: string;
  law_name: string;
  law_hierarchy: string;
  law_url: string;
  _rid?: string;
  _self?: string;
  _etag?: string;
  _attachments?: string;
  _ts?: number;
}

// API 回應介面
interface LawSampleApiResponse {
  success: boolean;
  data?: LawSample[];
  count?: number;
  message?: string;
  requestId?: string;
  documentId?: string;
  error?: string;
}

interface LawSampleCreateResponse {
  success: boolean;
  data?: { id: string; [key: string]: any };
  message?: string;
  requestId?: string;
  documentId?: string;
  error?: string;
}

@Component({
  selector: 'app-law-sample-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './law-sample-management.component.html',
  styleUrls: ['./law-sample-management.component.scss']
})
export class LawSampleManagementComponent implements OnInit {
  lawSamples: LawSample[] = [];
  filteredLawSamples: LawSample[] = [];
  searchKeyword: string = '';
  isLoading: boolean = false;
  isModalOpen: boolean = false;
  isEditMode: boolean = false;
  selectedLawSample: LawSample | null = null;

  // 主題篩選
  selectedTopic: string = '';
  topicOptions: string[] = [];

  // 表單資料
  formData: LawSample = {
    companyid: environment.companyid || '',
    topic: '',
    type: '',
    law_name: '',
    law_hierarchy: '',
    law_url: ''
  };

  // 表單驗證
  formErrors: { [key: string]: string } = {};

  // 法規位階選項
  hierarchyOptions = ['法律', '命令', '行政規則', '其他'];

  // API URL
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadLawSamples();
  }

  // 載入法規樣本資料
  loadLawSamples(): void {
    this.isLoading = true;
    this.getLawSamples().subscribe({
      next: (data) => {
        this.lawSamples = data;
        this.filteredLawSamples = data;
        this.updateTopicOptions();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  // 更新主題選項
  updateTopicOptions(): void {
    const topicSet = new Set(this.lawSamples.map(item => item.topic));
    this.topicOptions = Array.from(topicSet).filter(topic => topic.trim()).sort();
  }

  // API 方法 - 取得法規樣本列表
  getLawSamples(): Observable<LawSample[]> {
    const url = `${this.apiUrl}/api/regulation/law_sample/getsample`;


    return this.http.get<LawSampleApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.message || '取得法規樣本失敗');
        }
      }),
      catchError(this.handleError<LawSample[]>('取得法規樣本', []))
    );
  }

  // API 方法 - 新增法規樣本
  createLawSample(data: LawSample): Observable<any> {
    const url = `${this.apiUrl}/api/regulation/law_sample/add`;

    return this.http.post<LawSampleCreateResponse>(url, data).pipe(
      map(response => {
        if (response.success) {
          return { success: true, id: response.data?.id || this.generateId() };
        } else {
          throw new Error(response.message || '新增法規樣本失敗');
        }
      }),
      catchError(this.handleError<any>('新增法規樣本', { success: false }))
    );
  }

  // API 方法 - 更新法規樣本
  updateLawSample(id: string, data: LawSample): Observable<any> {
    const url = `${this.apiUrl}/api/regulation/law_sample/edit/${id}`;

    return this.http.put<LawSampleApiResponse>(url, data).pipe(
      map(response => {
        if (response.success) {
          return { success: true };
        } else {
          throw new Error(response.message || '更新法規樣本失敗');
        }
      }),
      catchError(this.handleError<any>('更新法規樣本', { success: false }))
    );
  }

  // API 方法 - 刪除法規樣本
  deleteLawSample(id: string): Observable<any> {
    const url = `${this.apiUrl}/api/regulation/law_sample/delete/${id}`;
    const params = {
      companyid: environment.companyid || ''
    };

    return this.http.delete<LawSampleApiResponse>(url, { params }).pipe(
      map(response => {
        if (response.success) {
          return { success: true };
        } else {
          throw new Error(response.message || '刪除法規樣本失敗');
        }
      }),
      catchError(this.handleError<any>('刪除法規樣本', { success: false }))
    );
  }

  // 搜尋功能
  onSearch(): void {
    this.filteredLawSamples = this.lawSamples.filter(item => {
      // 關鍵字篩選
      const keywordMatch = !this.searchKeyword.trim() ||
        item.law_name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        item.topic.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        item.type.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        item.law_hierarchy.toLowerCase().includes(this.searchKeyword.toLowerCase());

      // 主題篩選
      const topicMatch = !this.selectedTopic || item.topic === this.selectedTopic;

      return keywordMatch && topicMatch;
    });
  }

  // 主題篩選變更
  onTopicChange(): void {
    this.onSearch();
  }

  // 開啟新增模態框
  openAddModal(): void {
    this.isEditMode = false;
    this.isModalOpen = true;
    this.formData = {
      companyid: environment.companyid || '',
      topic: '',
      type: '',
      law_name: '',
      law_hierarchy: '',
      law_url: ''
    };
    this.formErrors = {};
  }

  // 開啟編輯模態框
  openEditModal(lawSample: LawSample): void {
    this.isEditMode = true;
    this.isModalOpen = true;
    this.selectedLawSample = lawSample;
    this.formData = { ...lawSample };
    this.formErrors = {};
  }

  // 關閉模態框
  closeModal(): void {
    this.isModalOpen = false;
    this.selectedLawSample = null;
    this.formData = {
      companyid: environment.companyid || '',
      topic: '',
      type: '',
      law_name: '',
      law_hierarchy: '',
      law_url: ''
    };
    this.formErrors = {};
  }

  // 表單驗證
  validateForm(): boolean {
    this.formErrors = {};
    let isValid = true;

    // 必填欄位驗證
    if (!this.formData.topic.trim()) {
      this.formErrors['topic'] = '主題為必填欄位';
      isValid = false;
    }

    if (!this.formData.type.trim()) {
      this.formErrors['type'] = '類型為必填欄位';
      isValid = false;
    }

    if (!this.formData.law_name.trim()) {
      this.formErrors['law_name'] = '法規名稱為必填欄位';
      isValid = false;
    }

    if (!this.formData.law_hierarchy.trim()) {
      this.formErrors['law_hierarchy'] = '法規位階為必填欄位';
      isValid = false;
    }

    if (!this.formData.law_url.trim()) {
      this.formErrors['law_url'] = '法規網址為必填欄位';
      isValid = false;
    }

    // URL 格式驗證
    if (this.formData.law_url.trim() && !this.isValidUrl(this.formData.law_url)) {
      this.formErrors['law_url'] = '請輸入有效的網址格式';
      isValid = false;
    }

    return isValid;
  }

  // URL 格式驗證
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // 儲存法規樣本
  saveLawSample(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;

    if (this.isEditMode && this.selectedLawSample) {
      // 編輯模式
      this.updateLawSample(this.selectedLawSample.id!, this.formData).subscribe({
        next: (response) => {
          if (response.success) {
            // 更新本地資料
            const index = this.lawSamples.findIndex(item => item.id === this.selectedLawSample!.id);
            if (index !== -1) {
              this.lawSamples[index] = { ...this.formData, id: this.selectedLawSample!.id };
            }
            this.updateTopicOptions();
            this.onSearch(); // 重新篩選
            this.closeModal();
            alert('法規樣本更新成功！');
            // 重新載入列表以確保資料同步
            this.loadLawSamples();
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
    } else {
      // 新增模式
      this.createLawSample(this.formData).subscribe({
        next: (response) => {
          if (response.success) {
            this.closeModal();
            alert('法規樣本新增成功！');
            // 重新載入完整列表以確保資料同步
            this.loadLawSamples();
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  // 刪除法規樣本
  deleteLawSampleConfirm(lawSample: LawSample): void {
    if (confirm(`確定要刪除法規樣本「${lawSample.law_name}」嗎？`)) {
      this.isLoading = true;
      this.deleteLawSample(lawSample.id!).subscribe({
        next: (response) => {
          if (response.success) {
            alert('法規樣本刪除成功！');
            // 重新載入完整列表以確保資料同步
            this.loadLawSamples();
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  // 產生唯一 ID
  private generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // 錯誤處理方法
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error(`${operation} 失敗:`, error);

      // 顯示使用者友善的錯誤訊息
      let errorMessage = `${operation}失敗`;

      if (error.error) {
        if (error.error.message) {
          errorMessage += `: ${error.error.message}`;
        } else if (error.error.error) {
          errorMessage += `: ${error.error.error}`;
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      } else {
        errorMessage += ': 未知錯誤';
      }

      alert(errorMessage);

      // 返回安全的結果讓應用程式繼續運作
      return of(result as T);
    };
  }
}
